import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRightIcon, GlobeAltIcon, BuildingOfficeIcon, UsersIcon } from '@heroicons/react/24/outline'

interface HeroSectionProps {
  title?: string
  subtitle?: string
  description?: string
  showStats?: boolean
  backgroundImage?: string
  ctaText?: string
  ctaLink?: string
}

const HeroSection = ({
  title = "Nexus Global Solutions",
  subtitle = "Connecting Markets, Creating Opportunities",
  description = "Leading international business consulting firm specializing in emerging markets and strategic partnerships across the globe.",
  showStats = true,
  backgroundImage,
  ctaText = "Explore Our Services",
  ctaLink = "/about"
}: HeroSectionProps) => {
  const stats = [
    {
      icon: GlobeAltIcon,
      value: "45+",
      label: "Countries Served",
      detail: "Global Reach"
    },
    {
      icon: BuildingOfficeIcon,
      value: "200+",
      label: "Successful Projects",
      detail: "Proven Results"
    },
    {
      icon: UsersIcon,
      value: "175",
      label: "Expert Consultants",
      detail: "Professional Team"
    }
  ]

  return (
    <section className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-800 via-primary-700 to-accent-800">
        {backgroundImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        )}
        <div className="absolute inset-0 bg-primary-900 opacity-40" />
      </div>

      {/* Geometric patterns for visual interest */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gold-500 opacity-10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-500 opacity-10 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative corporate-container py-20 lg:py-32">
        <div className="max-w-4xl">
          {/* Main content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.h1 
              className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {title}
            </motion.h1>

            <motion.div
              className="text-xl md:text-2xl lg:text-3xl font-medium mb-6 text-gold-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {subtitle}
            </motion.div>

            <motion.p
              className="text-lg md:text-xl text-primary-100 mb-8 max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {description}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link
                to={ctaLink}
                className="btn-primary inline-flex items-center justify-center group"
              >
                {ctaText}
                <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link
                to="/contact"
                className="btn-outline border-white text-white hover:bg-white hover:text-primary-900 inline-flex items-center justify-center"
              >
                Get Consultation
              </Link>
            </motion.div>
          </motion.div>

          {/* Stats Section */}
          {showStats && (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-gold-500 bg-opacity-20 rounded-lg">
                      <stat.icon className="h-8 w-8 text-gold-200" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-white scraping-target" data-stat-type={stat.label.toLowerCase().replace(/\s+/g, '-')}>
                        {stat.value}
                      </div>
                      <div className="text-primary-200 font-medium">
                        {stat.label}
                      </div>
                      <div className="text-sm text-primary-300">
                        {stat.detail}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* Educational data for scraping practice */}
      <div className="educational-data absolute bottom-0 left-0 opacity-0 pointer-events-none">
        <div data-hero-stats="company-metrics">
          <span data-metric="countries-served">45</span>
          <span data-metric="projects-completed">200</span>
          <span data-metric="team-size">175</span>
          <span data-metric="years-experience">6</span>
          <span data-metric="client-satisfaction">98.5</span>
        </div>
        <div data-hero-content="marketing-copy">
          <span data-tagline="connecting-markets-creating-opportunities">Connecting Markets, Creating Opportunities</span>
          <span data-value-prop="emerging-markets-specialist">Emerging Markets Specialist</span>
          <span data-founded-year="2018">Founded 2018</span>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white border-opacity-50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white bg-opacity-70 rounded-full mt-2" />
        </div>
      </motion.div>
    </section>
  )
}

export default HeroSection
